import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { ArrowLeft, ChevronDown, Coins, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { useWalletStore } from '@/stores/useWalletStore'
import { usePairsStore } from '@/stores/usePairsStore'
import { useEosTransaction } from '@/hooks/useEosTransaction'
import { TokenSelector } from '@/components/token/TokenSelector'
import { getPairId } from '@/lib/swap-lib'
import { eosTool } from '@/services/blockchain/eos-tool'
import { getCoinIcon, sleep } from '@/lib/common'
import type { TokenInfo } from '@/types'

function CoinIcon({ contract, coin }: { contract: string; coin: string }) {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!contract || !coin) {
      setUrl(null)
      return
    }
    const next = getCoinIcon(contract, coin)
    const timer = window.setTimeout(() => {
      const probe = new Image()
      probe.onload = () => setUrl(next)
      probe.onerror = () => setUrl(null)
      probe.src = next
    }, 1000)
    return () => window.clearTimeout(timer)
  }, [contract, coin])

  return (
    <div className="w-[24px] h-[24px] rounded-full bg-gray-100 shrink-0 flex items-center justify-center overflow-hidden">
      {url ? (
        <img src={url} alt={coin} className="w-full h-full object-cover" />
      ) : (
        <Coins className="h-3.5 w-3.5 text-nav" />
      )}
    </div>
  )
}

interface TokenInput {
  coin: string
  contract: string
  precision: number
}

const DEFAULT_TOKEN0: TokenInput = { coin: 'EOS', contract: 'eosio.token', precision: 4 }
const EMPTY_TOKEN: TokenInput = { coin: '', contract: '', precision: 4 }

export function CreateMarket() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isLogin, session } = useWalletStore()
  const { pairs, coins, fetchPairs } = usePairsStore()
  const { transact, loading: txLoading } = useEosTransaction()

  const [token0, setToken0] = useState<TokenInput>(DEFAULT_TOKEN0)
  const [token1, setToken1] = useState<TokenInput>(EMPTY_TOKEN)
  const [selectorType, setSelectorType] = useState<'token0' | 'token1' | null>(null)

  const setToken = (type: 'token0' | 'token1', next: TokenInput) => {
    if (type === 'token0') setToken0(next)
    else setToken1(next)
  }

  const handleTokenSelect = (token: TokenInfo) => {
    if (!selectorType) return
    setToken(selectorType, {
      coin: token.coin,
      contract: token.contract,
      precision: token.precision,
    })
  }

  const fetchPrecision = async (token: TokenInput): Promise<number | null> => {
    try {
      const stats = await eosTool.getCurrencyStats(token.contract.toLowerCase(), token.coin.toUpperCase())
      const supply = stats?.supply || stats?.max_supply
      if (!supply) return null
      const amount = supply.split(' ')[0]
      const dot = amount.indexOf('.')
      return dot === -1 ? 0 : amount.length - dot - 1
    } catch {
      return null
    }
  }

  const handleSubmit = async () => {
    if (!isLogin || !session) {
      toast.warning(t('public.noauth'))
      return
    }

    const t0: TokenInput = {
      coin: token0.coin.trim().toUpperCase(),
      contract: token0.contract.trim().toLowerCase(),
      precision: token0.precision,
    }
    const t1: TokenInput = {
      coin: token1.coin.trim().toUpperCase(),
      contract: token1.contract.trim().toLowerCase(),
      precision: token1.precision,
    }

    if (!t0.coin || !t0.contract || !t1.coin || !t1.contract) {
      toast.warning(t('swap.tokenInfoEmpty'))
      return
    }
    if (t0.coin === t1.coin && t0.contract === t1.contract) {
      toast.error(t('swap.sameTokenError'))
      return
    }

    const p0 = await fetchPrecision(t0)
    if (p0 === null) { toast.error(`${t0.coin} ${t('swap.tokenNotFound')}`); return }
    t0.precision = p0

    const p1 = await fetchPrecision(t1)
    if (p1 === null) { toast.error(`${t1.coin} ${t('swap.tokenNotFound')}`); return }
    t1.precision = p1

    const existingId = getPairId(t0, t1, pairs)
    if (existingId !== null && existingId !== undefined) {
      toast.warning(t('swap.pairExistsError'))
      return
    }

    const account = session.account
    const actions = [{
      account: 'swap.ttr',
      name: 'createpair',
      authorization: [{ actor: account.name, permission: account.authority || 'active' }],
      data: {
        creator: account.name,
        token0: { sym: `${t0.precision},${t0.coin}`, contract: t0.contract },
        token1: { sym: `${t1.precision},${t1.coin}`, contract: t1.contract },
      },
    }]

    const result = await transact(actions, t('swap.createMarketSuccess'))
    if (result) {
      await sleep(500)
      await fetchPairs()
      navigate({ to: '/pool' })
    }
  }

  const renderTokenCard = (
    label: string,
    type: 'token0' | 'token1',
    value: TokenInput,
  ) => (
    <div className="bg-[#f9faf9] rounded-2xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[13px] text-nav">{label}</span>
        <button
          onClick={() => setSelectorType(type)}
          className="flex items-center gap-1 text-[13px] text-primary hover:opacity-80 transition-opacity"
        >
          {t('swap.selectToken')}
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      <div className="flex items-center gap-2 h-[44px] bg-white rounded-xl border border-[#e5e5e5] px-3">
        <CoinIcon contract={value.contract} coin={value.coin} />
        <input
          type="text"
          value={value.coin}
          onChange={(e) => setToken(type, { ...value, coin: e.target.value })}
          placeholder={t('swap.enterTokenName')}
          className="flex-1 bg-transparent text-[14px] font-semibold text-foreground outline-none placeholder:text-nav/50 placeholder:font-normal uppercase"
        />
      </div>

      <input
        type="text"
        value={value.contract}
        onChange={(e) => setToken(type, { ...value, contract: e.target.value })}
        placeholder={t('swap.enterContractAddress')}
        className="w-full h-[44px] bg-white rounded-xl border border-[#e5e5e5] px-3 text-[14px] text-foreground outline-none placeholder:text-nav/50 lowercase"
      />
    </div>
  )

  const buttonText = () => {
    if (!isLogin) return t('public.login')
    if (!token0.coin || !token0.contract || !token1.coin || !token1.contract) return t('swap.tokenInfoEmpty')
    return t('swap.createMarket')
  }

  const isDisabled = !isLogin || !token0.coin || !token0.contract || !token1.coin || !token1.contract || txLoading

  return (
    <div className="w-full max-w-[460px] mx-auto px-4 py-6 md:py-10 animate-trade-enter">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate({ to: '/pool' })} className="p-1.5 rounded-full hover:bg-[#edf5ed] transition-colors">
          <ArrowLeft className="h-5 w-5 text-foreground" />
        </button>
        <h2 className="text-[20px] font-bold text-foreground">{t('swap.createMarket')}</h2>
      </div>

      <div className="bg-white rounded-2xl border border-[#e5e5e5] p-5 space-y-4">
        {renderTokenCard('Token A', 'token0', token0)}

        <div className="flex justify-center">
          <div className="w-[42px] h-[42px] rounded-full bg-[#498846] flex items-center justify-center">
            <Plus className="h-5 w-5 text-white" />
          </div>
        </div>

        {renderTokenCard('Token B', 'token1', token1)}

      </div>

      <div className="mt-[21px]">
        <button
          onClick={handleSubmit}
          disabled={isDisabled}
          className="w-full h-[48px] rounded-[24px] bg-[#498846] text-white text-[18px] font-semibold cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-95 transition-opacity flex items-center justify-center"
        >
          {txLoading ? t('public.loading') : buttonText()}
        </button>
      </div>

      <TokenSelector
        open={selectorType !== null}
        onClose={() => setSelectorType(null)}
        onSelect={handleTokenSelect}
        coins={coins}
      />
    </div>
  )
}
